import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { getTemplates } from '../store/slices/templateSlice';
import type { TemplateItem } from '../store/slices/templateSlice';
import MarkdownPreview from './MarkdownPreview';

export interface NoteFieldWithContextMenuProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  /** Optional class for the wrapper div (e.g. flex-1 for flex layouts). */
  wrapperClassName?: string;
  /** Called when user chooses "Şablon oluştur" in the template dropdown; e.g. open create-template modal. */
  onCreateTemplate?: () => void;
  /** When true, show "Sadece önizlemeyi göster" toggle (e.g. only in password edit modal). */
  showPreviewOnlyToggle?: boolean;
  /** When true, never show source and preview side by side; show one at a time with Kaynak/Önizleme switch (e.g. add password). */
  singleViewMode?: boolean;
  /** When singleViewMode is true, initial tab to show. If not set, uses storage when showPreviewOnlyToggle is true else 'source'. */
  defaultView?: 'source' | 'preview';
}

const DEFAULT_TABLE_MARKDOWN = `| | |
|---|---|
| | |
`;

const CODE_BLOCK_MARKDOWN = '```\n\n```';
const BLOCKQUOTE_PREFIX = '> ';
const BULLET_ITEM = '- ';
const NUMBERED_ITEM = '1. ';

const PREVIEW_ONLY_STORAGE_KEY = 'smartpm.notes.previewOnly';

function getPreviewOnlyFromStorage(): boolean {
  try {
    return localStorage.getItem(PREVIEW_ONLY_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

const NoteFieldWithContextMenu: React.FC<NoteFieldWithContextMenuProps> = ({
  value,
  onChange,
  className,
  wrapperClassName,
  onCreateTemplate,
  showPreviewOnlyToggle = false,
  singleViewMode = false,
  defaultView,
  ...textareaProps
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const templateItems = useAppSelector((state) => state.template.items);
  const templateListLoading = useAppSelector((state) => state.template.operationLoading.list);

  const [showPreviewOnly, setShowPreviewOnly] = useState(getPreviewOnlyFromStorage);
  /** singleViewMode: 'source' | 'preview'; only used when singleViewMode is true. defaultView overrides; else "Sadece önizleme" tercihine göre. */
  const [singleView, setSingleView] = useState<'source' | 'preview'>(() => {
    if (singleViewMode && defaultView) return defaultView;
    return singleViewMode && showPreviewOnlyToggle && getPreviewOnlyFromStorage() ? 'preview' : 'source';
  });
  const effectivePreviewOnly = singleViewMode
    ? singleView === 'preview'
    : showPreviewOnlyToggle && showPreviewOnly;
  const effectiveSourceVisible = singleViewMode ? singleView === 'source' : !effectivePreviewOnly;
  const [menuOpen, setMenuOpen] = useState<{ x: number; y: number } | null>(null);
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [templateButtonOpen, setTemplateButtonOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const templateButtonWrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);
  const caretAfterInsertRef = useRef<number | null>(null);

  const closeMenu = useCallback(() => {
    setMenuOpen(null);
    setShowTemplateList(false);
    setTemplateButtonOpen(false);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (menuOpen || templateButtonOpen) {
      dispatch(getTemplates());
    }
  }, [menuOpen, templateButtonOpen, dispatch]);

  useEffect(() => {
    if (!templateButtonOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        templateButtonWrapperRef.current &&
        !templateButtonWrapperRef.current.contains(e.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setTemplateButtonOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [templateButtonOpen]);

  const syncSelectionFromTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      selectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
    } else {
      selectionRef.current = null;
    }
  }, []);

  const handleTemplateButtonClick = useCallback(() => {
    syncSelectionFromTextarea();
    setTemplateButtonOpen((v) => !v);
  }, [syncSelectionFromTextarea]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      syncSelectionFromTextarea();
      setMenuOpen({ x: e.clientX, y: e.clientY });
      setShowTemplateList(false);
    },
    [syncSelectionFromTextarea]
  );

  const handleApplyTemplate = useCallback(
    (template: TemplateItem) => {
      const content = template.content ?? '';
      const sel = selectionRef.current;
      const start = sel ? sel.start : value.length;
      const end = sel ? sel.end : value.length;
      const newValue = value.slice(0, start) + content + value.slice(end);
      caretAfterInsertRef.current = start + content.length;
      onChange(newValue);
      selectionRef.current = null;
      closeMenu();
    },
    [onChange, closeMenu, value]
  );

  const insertAtCursor = useCallback(
    (toInsert: string, cursorOffset: number) => {
      const el = textareaRef.current;
      const start = el ? el.selectionStart : value.length;
      const end = el ? el.selectionEnd : value.length;
      const newValue = value.slice(0, start) + toInsert + value.slice(end);
      caretAfterInsertRef.current = start + cursorOffset;
      onChange(newValue);
    },
    [value, onChange]
  );

  const handleBold = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    const newText = selected ? `**${selected}**` : '****';
    const newValue = value.slice(0, start) + newText + value.slice(end);
    const newCursor = selected ? start + newText.length : start + 2;
    caretAfterInsertRef.current = newCursor;
    onChange(newValue);
  }, [value, onChange]);

  const handleInsertTable = useCallback(() => {
    insertAtCursor(DEFAULT_TABLE_MARKDOWN, DEFAULT_TABLE_MARKDOWN.length);
  }, [insertAtCursor]);

  const wrapSelection = useCallback(
    (before: string, after: string, emptyPlaceholder: string) => {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const selected = value.slice(start, end);
      const newText = selected ? `${before}${selected}${after}` : emptyPlaceholder;
      const newValue = value.slice(0, start) + newText + value.slice(end);
      const newCursor = selected ? start + newText.length : start + before.length;
      caretAfterInsertRef.current = newCursor;
      onChange(newValue);
    },
    [value, onChange]
  );

  const handleItalic = useCallback(() => wrapSelection('*', '*', '**'), [wrapSelection]);
  const handleStrikethrough = useCallback(() => wrapSelection('~~', '~~', '~~~~'), [wrapSelection]);
  const handleInlineCode = useCallback(() => wrapSelection('`', '`', '``'), [wrapSelection]);

  const insertLinePrefix = useCallback(
    (prefix: string) => {
      const el = textareaRef.current;
      const pos = el ? el.selectionStart : value.length;
      const lineStart = value.slice(0, pos).split('\n').pop()?.length ?? 0;
      const lineStartIndex = pos - lineStart;
      const newValue = value.slice(0, lineStartIndex) + prefix + value.slice(lineStartIndex);
      caretAfterInsertRef.current = lineStartIndex + prefix.length;
      onChange(newValue);
    },
    [value, onChange]
  );

  const handleHeading = useCallback(
    (level: 1 | 2 | 3) => {
      const prefix = '#'.repeat(level) + ' ';
      insertLinePrefix(prefix);
    },
    [insertLinePrefix]
  );

  const handleBlockquote = useCallback(() => insertLinePrefix(BLOCKQUOTE_PREFIX), [insertLinePrefix]);
  const handleBulletList = useCallback(() => insertLinePrefix(BULLET_ITEM), [insertLinePrefix]);
  const handleNumberedList = useCallback(() => insertLinePrefix(NUMBERED_ITEM), [insertLinePrefix]);

  const handleCodeBlock = useCallback(() => {
    insertAtCursor(CODE_BLOCK_MARKDOWN, CODE_BLOCK_MARKDOWN.length - 3);
  }, [insertAtCursor]);

  const handleLink = useCallback(() => {
    const el = textareaRef.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const selected = value.slice(start, end);
    const text = selected || t('forms.notes.linkPlaceholder', 'metin');
    const toInsert = `[${text}](url)`;
    const newValue = value.slice(0, start) + toInsert + value.slice(end);
    caretAfterInsertRef.current = start + toInsert.length - 4;
    onChange(newValue);
  }, [value, onChange, t]);

  const handleTogglePreviewOnly = useCallback(() => {
    setShowPreviewOnly((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PREVIEW_ONLY_STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const caret = caretAfterInsertRef.current;
    if (caret != null && textareaRef.current) {
      caretAfterInsertRef.current = null;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(caret, caret);
    }
  }, [value]);

  return (
    <div className={wrapperClassName ? `relative flex flex-col min-h-0 ${wrapperClassName}` : 'relative flex flex-col min-h-0'}>
      <div className="flex flex-col gap-1.5 flex-shrink-0 mb-1.5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <div ref={templateButtonWrapperRef} className="relative">
              <button
                type="button"
                onClick={handleTemplateButtonClick}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${templateButtonOpen ? 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:border-gray-200 dark:hover:border-gray-600'}`}
                title={t('pages.passwords.templateModal.applyTemplate')}
                aria-label={t('pages.passwords.templateModal.applyTemplate')}
                aria-expanded={templateButtonOpen}
              >
                <i className="fa-solid fa-file-lines" />
                <span>{t('pages.passwords.templateModal.applyTemplate')}</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform ${templateButtonOpen ? 'rotate-180' : ''}`} />
              </button>
              {templateButtonOpen && (
                <div className="absolute left-0 top-full mt-1 z-[100] min-w-[200px] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1 max-h-56 overflow-hidden flex flex-col">
                  <div className="overflow-y-auto max-h-48">
                    {templateListLoading ? (
                      <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        <i className="fa-solid fa-spinner fa-spin mr-1" />
                        {t('common.loading')}
                      </div>
                    ) : templateItems.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {t('pages.passwords.templateModal.noTemplates')}
                      </div>
                    ) : (
                      templateItems.map((item: TemplateItem) => (
                        <button
                          key={item.id}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 truncate"
                          onClick={() => handleApplyTemplate(item)}
                        >
                          {item.name || t('pages.passwords.templateModal.title')}
                        </button>
                      ))
                    )}
                  </div>
                  {onCreateTemplate && (
                    <>
                      <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-primary/10 dark:hover:bg-primary/20 flex items-center gap-2"
                        onClick={() => {
                          onCreateTemplate();
                          closeMenu();
                        }}
                      >
                        <i className="fa-solid fa-plus" />
                        {t('pages.passwords.createTemplate')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {singleViewMode && (
              <div className="inline-flex items-center gap-2 flex-wrap">
                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-800/80" role="tablist" aria-label={t('forms.notes.viewMode', 'Görünüm')}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={singleView === 'source'}
                    onClick={() => {
                      setSingleView('source');
                      if (showPreviewOnlyToggle) {
                        setShowPreviewOnly(false);
                        try {
                          localStorage.setItem(PREVIEW_ONLY_STORAGE_KEY, 'false');
                        } catch {
                          /* ignore */
                        }
                      }
                    }}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${singleView === 'source' ? 'bg-white dark:bg-gray-700 shadow-sm text-primary border border-gray-200 dark:border-gray-600' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
                  >
                    <i className="fa-solid fa-code text-xs" />
                    <span>{t('forms.notes.tabSource', 'Kaynak')}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={singleView === 'preview'}
                    onClick={() => {
                      setSingleView('preview');
                      if (showPreviewOnlyToggle) {
                        setShowPreviewOnly(true);
                        try {
                          localStorage.setItem(PREVIEW_ONLY_STORAGE_KEY, 'true');
                        } catch {
                          /* ignore */
                        }
                      }
                    }}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${singleView === 'preview' ? 'bg-white dark:bg-gray-700 shadow-sm text-primary border border-gray-200 dark:border-gray-600' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
                  >
                    <i className="fa-solid fa-eye text-xs" />
                    <span>{t('forms.notes.tabPreview', 'Önizleme')}</span>
                  </button>
                </div>
                {showPreviewOnlyToggle && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = !showPreviewOnly;
                      setShowPreviewOnly(next);
                      setSingleView(next ? 'preview' : 'source');
                      try {
                        localStorage.setItem(PREVIEW_ONLY_STORAGE_KEY, String(next));
                      } catch {
                        /* ignore */
                      }
                    }}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${showPreviewOnly ? 'bg-primary/15 dark:bg-primary/25 border-primary/50 text-primary' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:border-gray-200 dark:hover:border-gray-600'}`}
                    title={t('forms.notes.showPreviewOnly', 'Sadece önizleme')}
                    aria-pressed={showPreviewOnly}
                  >
                    <i className="fa-solid fa-eye text-xs" />
                    <span>{t('forms.notes.showPreviewOnly', 'Sadece önizleme')}</span>
                  </button>
                )}
              </div>
            )}
            {!singleViewMode && showPreviewOnlyToggle && (
              <button
                type="button"
                onClick={handleTogglePreviewOnly}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${showPreviewOnly ? 'bg-primary/15 dark:bg-primary/25 border-primary/50 text-primary' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:border-gray-200 dark:hover:border-gray-600'}`}
                title={t('forms.notes.showPreviewOnly', 'Sadece önizleme')}
                aria-pressed={showPreviewOnly}
              >
                <i className="fa-solid fa-eye text-xs" />
                <span>{t('forms.notes.showPreviewOnly', 'Sadece önizleme')}</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-0.5 flex-wrap">
            <button type="button" onClick={handleBold} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.bold', 'Kalın')} aria-label={t('forms.notes.bold', 'Kalın')}>
              <i className="fa-solid fa-bold" />
            </button>
            <button type="button" onClick={handleItalic} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.italic', 'İtalik')} aria-label={t('forms.notes.italic', 'İtalik')}>
              <i className="fa-solid fa-italic" />
            </button>
            <button type="button" onClick={handleStrikethrough} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.strikethrough', 'Üstü çizili')} aria-label={t('forms.notes.strikethrough', 'Üstü çizili')}>
              <i className="fa-solid fa-strikethrough" />
            </button>
            <button type="button" onClick={() => handleHeading(1)} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.heading1', 'Başlık 1')} aria-label={t('forms.notes.heading1', 'Başlık 1')}>
              <i className="fa-solid fa-heading" />
              <span className="text-[10px] ml-0.5 align-sub">1</span>
            </button>
            <button type="button" onClick={() => handleHeading(2)} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.heading2', 'Başlık 2')} aria-label={t('forms.notes.heading2', 'Başlık 2')}>
              <span className="text-xs font-bold">H2</span>
            </button>
            <button type="button" onClick={() => handleHeading(3)} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.heading3', 'Başlık 3')} aria-label={t('forms.notes.heading3', 'Başlık 3')}>
              <span className="text-xs font-bold">H3</span>
            </button>
            <button type="button" onClick={handleBulletList} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.bulletList', 'Madde listesi')} aria-label={t('forms.notes.bulletList', 'Madde listesi')}>
              <i className="fa-solid fa-list-ul" />
            </button>
            <button type="button" onClick={handleNumberedList} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.numberedList', 'Numaralı liste')} aria-label={t('forms.notes.numberedList', 'Numaralı liste')}>
              <i className="fa-solid fa-list-ol" />
            </button>
            <button type="button" onClick={handleLink} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.link', 'Link')} aria-label={t('forms.notes.link', 'Link')}>
              <i className="fa-solid fa-link" />
            </button>
            <button type="button" onClick={handleInlineCode} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.inlineCode', 'Kod')} aria-label={t('forms.notes.inlineCode', 'Kod')}>
              <i className="fa-solid fa-code" />
            </button>
            <button type="button" onClick={handleCodeBlock} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.codeBlock', 'Kod bloğu')} aria-label={t('forms.notes.codeBlock', 'Kod bloğu')}>
              <i className="fa-solid fa-square-code" />
            </button>
            <button type="button" onClick={handleBlockquote} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.blockquote', 'Alıntı')} aria-label={t('forms.notes.blockquote', 'Alıntı')}>
              <i className="fa-solid fa-quote-right" />
            </button>
            <button type="button" onClick={handleInsertTable} className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-colors" title={t('forms.notes.insertTable', 'Tablo ekle')} aria-label={t('forms.notes.insertTable', 'Tablo ekle')}>
              <i className="fa-solid fa-table" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-3">
        <div className={`flex-1 min-h-0 grid gap-3 ${singleViewMode || effectivePreviewOnly ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'} ${singleViewMode ? 'min-h-[280px] sm:min-h-[400px] lg:min-h-[520px]' : className?.includes('min-h-') ? '' : 'min-h-[240px] sm:min-h-[360px]'}`}>
          {effectiveSourceVisible && (
            <div className={`flex flex-col min-h-0 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden ${singleViewMode ? 'min-h-[280px] sm:min-h-[400px] lg:min-h-[520px]' : ''}`}>
              <div className="px-2 py-1.5 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/80 text-xs font-medium text-gray-600 dark:text-gray-400 flex-shrink-0">
                {t('forms.notes.tabSource', 'Kaynak')}
              </div>
              <div className="flex-1 min-h-0 p-2">
                <textarea
                  ref={textareaRef}
                  {...textareaProps}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onContextMenu={handleContextMenu}
                  className={`w-full h-full min-h-[180px] sm:min-h-[280px] resize-none rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none font-['JetBrains_Mono',monospace] ${className?.replace('min-h-[200px]', '').replace('min-h-[480px]', '') ?? ''}`}
                  placeholder={t('forms.notes.placeholder', 'Not ekleyin...')}
                />
              </div>
            </div>
          )}
          {(!singleViewMode || effectivePreviewOnly) && (
            <div className={`flex flex-col min-h-0 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 overflow-hidden ${singleViewMode ? 'min-h-[280px] sm:min-h-[400px] lg:min-h-[520px]' : ''}`}>
              <div className="px-2 py-1.5 border-b border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800/80 text-xs font-medium text-gray-600 dark:text-gray-400 flex-shrink-0">
                {t('forms.notes.tabPreview', 'Önizleme')}
              </div>
              <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
                <MarkdownPreview source={value} className="text-sm" />
              </div>
            </div>
          )}
        </div>
      </div>

      {menuOpen && (
        <div
          ref={menuRef}
          className="fixed z-[100] min-w-[180px] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1"
          style={{ left: menuOpen.x, top: menuOpen.y }}
        >
          <button
            type="button"
            className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between gap-2"
            onClick={() => setShowTemplateList((v) => !v)}
          >
            <span>
              <i className="fa-solid fa-file-lines mr-2 text-gray-500" />
              {t('pages.passwords.templateModal.applyTemplate')}
            </span>
            <i className={`fa-solid fa-chevron-right text-xs ${showTemplateList ? 'rotate-90' : ''}`} />
          </button>
          {showTemplateList && (
            <>
              <div className="border-t border-gray-100 dark:border-gray-700 max-h-48 overflow-y-auto">
                {templateListLoading ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    <i className="fa-solid fa-spinner fa-spin mr-1" />
                    {t('common.loading')}
                  </div>
                ) : templateItems.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {t('pages.passwords.templateModal.noTemplates')}
                  </div>
                ) : (
                  templateItems.map((item: TemplateItem) => (
                    <button
                      key={item.id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 truncate"
                      onClick={() => handleApplyTemplate(item)}
                    >
                      {item.name || t('pages.passwords.templateModal.title')}
                    </button>
                  ))
                )}
              </div>
              {onCreateTemplate && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-700" />
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-primary/10 dark:hover:bg-primary/20 flex items-center gap-2"
                    onClick={() => {
                      onCreateTemplate();
                      closeMenu();
                    }}
                  >
                    <i className="fa-solid fa-plus" />
                    {t('pages.passwords.createTemplate')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NoteFieldWithContextMenu;
